/**
 * Custom hook for managing content selection and bulk operations
 */
import { useState, useCallback } from 'react';
import { Content } from '../useContentData';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';

export interface UseContentSelectionProps {
  contents: Content[];
  paginatedContents: Content[];
  onRefresh: () => Promise<void>;
}

export interface UseContentSelectionReturn {
  // Selection state
  selectedContent: Set<string>;
  setSelectedContent: (selected: Set<string>) => void;
  isSelectionMode: boolean;
  setIsSelectionMode: (isSelectionMode: boolean) => void;
  
  // Selection actions
  toggleSelectionMode: () => void;
  toggleContentSelection: (contentId: string) => void;
  toggleSelectAll: () => void;
  
  // Bulk operations
  bulkCancelScheduled: () => Promise<void>;
}

/**
 * Hook for managing content selection and bulk operations
 */
export const useContentSelection = ({ 
  contents, 
  paginatedContents, 
  onRefresh 
}: UseContentSelectionProps): UseContentSelectionReturn => {
  // Selection state
  const [selectedContent, setSelectedContent] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  /**
   * Toggle selection mode on/off and clear selections when turning off
   */
  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode(!isSelectionMode);
    if (isSelectionMode) {
      setSelectedContent(new Set());
    }
  }, [isSelectionMode]);

  /**
   * Toggle selection for a specific content item
   */
  const toggleContentSelection = useCallback((contentId: string) => {
    const newSelection = new Set(selectedContent);
    if (newSelection.has(contentId)) {
      newSelection.delete(contentId);
    } else {
      newSelection.add(contentId);
    }
    setSelectedContent(newSelection);
  }, [selectedContent]);

  /**
   * Select/Deselect all visible content on current page
   */
  const toggleSelectAll = useCallback(() => {
    if (selectedContent.size === paginatedContents.length) {
      setSelectedContent(new Set());
    } else {
      setSelectedContent(new Set(paginatedContents.map(c => c.id)));
    }
  }, [selectedContent.size, paginatedContents]);

  /**
   * Bulk cancel scheduled posts
   */
  const bulkCancelScheduled = useCallback(async () => {
    console.log('Bulk cancel scheduled - selected IDs:', Array.from(selectedContent));
    const selectedItems = contents.filter(c => selectedContent.has(c.id));
    console.log('Selected items:', selectedItems);
    
    // Use the computed display_status and is_scheduled flag for more accurate filtering
    const scheduledItems = selectedItems.filter(c => 
      (c.display_status === 'SCHEDULED' || c.is_scheduled === true) && 
      c.scheduled_time && !c.published_at
    );
    console.log('Scheduled items to cancel:', scheduledItems);
    
    if (scheduledItems.length === 0) {
      toast({
        title: "No scheduled content selected",
        description: "Please select scheduled content to cancel",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: session } = await supabase.auth.getSession();
      let successCount = 0;
      
      // Cancel each scheduled post using the proper API endpoint
      for (const item of scheduledItems) {
        // First check if there's a scheduled_post record
        const { data: scheduledPost } = await supabase
          .from('scheduled_social_posts')
          .select('id')
          .eq('content_id', item.id)
          .eq('status', 'SCHEDULED')
          .single();
        
        if (scheduledPost) {
          console.log('Canceling via API for scheduled post:', scheduledPost.id);
          // Use the API to cancel the scheduled post
          const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/api/autopost/cancel-publishing`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.session?.access_token}`,
            },
            body: JSON.stringify({
              scheduled_post_id: scheduledPost.id
            }),
          });

          if (response.ok) {
            successCount++;
          } else {
            console.error(`Failed to cancel scheduled post ${scheduledPost.id}`);
          }
        }
      }
      
      if (successCount > 0) {
        toast({
          title: "Scheduled posts canceled",
          description: `Successfully canceled ${successCount} scheduled post${successCount > 1 ? 's' : ''}`,
        });
        
        // Clear selection and refresh content list
        setSelectedContent(new Set());
        setIsSelectionMode(false);
        await onRefresh();
      } else {
        toast({
          title: "No posts canceled",
          description: "No scheduled posts were found to cancel",
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error('Error bulk canceling scheduled posts:', error);
      toast({
        title: "Error",
        description: "Failed to cancel scheduled posts",
        variant: "destructive",
      });
    }
  }, [selectedContent, contents, onRefresh]);

  return {
    selectedContent,
    setSelectedContent,
    isSelectionMode,
    setIsSelectionMode,
    toggleSelectionMode,
    toggleContentSelection,
    toggleSelectAll,
    bulkCancelScheduled,
  };
};